import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styles from './Winners.module.css';
import ReactConfetti from 'react-confetti';

const Winners = ({ lang }) => {
  const t = {
    en: {
      header: 'Our Lucky Winners',
      desc: 'Congratulations to all our winners! Check out who won what in our daily events.',
      loading: 'Loading winners...',
      error: 'Failed to load data',
      nextAnnouncement: 'Next Winner Announcement',
      nextOn: 'Next announcement on:',
      days: 'Days',
      hours: 'Hours',
      minutes: 'Minutes',
      seconds: 'Seconds',
      noWinners: 'No winners announced yet. Stay tuned!',
    },
    ar: {
      header: 'الفائزون المحظوظون',
      desc: 'مبروك لكل الفائزين! اكتشف من فاز في فعالياتنا اليومية.',
      loading: 'جاري تحميل الفائزين...',
      error: 'فشل تحميل البيانات',
      nextAnnouncement: 'الإعلان عن الفائز القادم',
      nextOn: 'الإعلان القادم في:',
      days: 'أيام',
      hours: 'ساعات',
      minutes: 'دقائق',
      seconds: 'ثواني',
      noWinners: 'لم يتم الإعلان عن فائزين بعد. ترقبوا!',
    },
    tr: {
      header: 'Şanslı Kazananlarımız',
      desc: 'Tüm kazananlarımızı tebrik ederiz! Günlük etkinliklerimizde kim ne kazandı öğrenin.',
      loading: 'Kazananlar yükleniyor...',
      error: 'Veriler yüklenemedi',
      nextAnnouncement: 'Sonraki Kazanan Duyurusu',
      nextOn: 'Sonraki duyuru:',
      days: 'Gün',
      hours: 'Saat',
      minutes: 'Dakika',
      seconds: 'Saniye',
      noWinners: 'Henüz kazanan yok. Takipte kalın!',
    }
  }[lang];
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [nextAnnouncement, setNextAnnouncement] = useState(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchWinners = async () => {
      try {
        const winnersQuery = query(collection(db, 'winners'), orderBy('announcementDateTime', 'desc'));
        const winnersSnapshot = await getDocs(winnersQuery);
        const now = new Date().getTime();

        // Filter winners to only show those past their announcement time
        const winnersData = winnersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(winner => {
            const announcementTime = new Date(winner.announcementDateTime).getTime();
            return announcementTime <= now;
          });

        // Check if there's a newly announced winner (within last 30 seconds)
        const hasNewWinner = winnersData.some(winner => {
          const announcementTime = new Date(winner.announcementDateTime).getTime();
          return (now - announcementTime) <= 30000; // 30 seconds
        });

        if (hasNewWinner) {
          setShowConfetti(true);
          // Stop confetti after 10 seconds
          setTimeout(() => setShowConfetti(false), 10000);
        }

        setWinners(winnersData);

        // Find next announcement
        const nextWinner = winnersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .find(winner => {
            const announcementTime = new Date(winner.announcementDateTime).getTime();
            return announcementTime > now;
          });

        if (nextWinner) {
          setNextAnnouncement(nextWinner.announcementDateTime);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchWinners();
    const interval = setInterval(fetchWinners, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!nextAnnouncement) return;

    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const announcementTime = new Date(nextAnnouncement).getTime();
      const difference = announcementTime - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        // Refresh winners list when countdown reaches zero
        window.location.reload();
      }
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft(); // Initial calculation

    return () => clearInterval(timer);
  }, [nextAnnouncement]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        <p>{t.error}</p>
      </div>
    );
  }

  return (
    <div className={styles.winnersPage}>
      {showConfetti && (
        <ReactConfetti
          width={windowDimensions.width}
          height={windowDimensions.height}
          numberOfPieces={200}
          recycle={false}
          run={showConfetti}
        />
      )}

      <div className={styles.header}>
        <h1>{t.header}</h1>
        <p>{t.desc}</p>
      </div>

      {nextAnnouncement && (
        <div className={styles.countdown}>
          <h2 className={styles.countdownTitle}>{t.nextAnnouncement}</h2>
          <div className={styles.countdownTimer}>
            <div className={styles.countdownItem}>
              <span className={styles.countdownNumber}>{timeLeft.days}</span>
              <span className={styles.countdownLabel}>{t.days}</span>
            </div>
            <div className={styles.countdownItem}>
              <span className={styles.countdownNumber}>{timeLeft.hours}</span>
              <span className={styles.countdownLabel}>{t.hours}</span>
            </div>
            <div className={styles.countdownItem}>
              <span className={styles.countdownNumber}>{timeLeft.minutes}</span>
              <span className={styles.countdownLabel}>{t.minutes}</span>
            </div>
            <div className={styles.countdownItem}>
              <span className={styles.countdownNumber}>{timeLeft.seconds}</span>
              <span className={styles.countdownLabel}>{t.seconds}</span>
            </div>
          </div>
          <div className={styles.nextAnnouncement}>
            {t.nextOn}
            <span className={styles.announcementTime}>
              {new Date(nextAnnouncement).toLocaleString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                timeZoneName: 'short'
              })}
            </span>
          </div>
        </div>
      )}

      {winners.length === 0 ? (
        <div className={styles.noWinners}>
          <p>{t.noWinners}</p>
        </div>
      ) : (
        <div className={styles.winnersGrid}>
          {winners.map(winner => (
            <div key={winner.id} className={styles.winnerCard}>
              <div className={styles.winnerImage}>
                <img src={winner.imageUrl} alt={winner.name} />
              </div>
              <div className={styles.winnerDetails}>
                <h2>{winner.name}</h2>
                <div className={styles.prizeInfo}>
                  <span className={styles.prizeLabel}>Won</span>
                  <span className={styles.prize}>{winner.prize}</span>
                </div>
                <div className={styles.dateInfo}>
                  <span className={styles.dateLabel}>Announced</span>
                  <span className={styles.date}>
                    {new Date(winner.announcementDateTime).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Winners;